// DSR Admin — internal user accounts list + invite. Gated on
// settings.manage_users. Mirrors the VendorsPage/PortalUsersPage list+modal
// pattern. Editing an account (profile, roles, person link, account actions)
// happens on the detail page.

import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { CreateAdminUserInput } from '@dsr/shared-types';
import { formatPhoneDisplay } from '@dsr/shared-types';
import {
  useAdminUsers,
  useCreateAdminUser,
  useRoles,
  type AdminUsersFilters,
} from '../api/queries/admin';
import { PhoneInput } from '../components/PhoneInput';
import { PersonPicker } from '../components/PersonPicker';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  Dialog,
  FormField,
  Input,
  Table,
} from '../components/ui';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';

export function AdminUsersPage(): JSX.Element {
  const [q, setQ] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);

  const filters: AdminUsersFilters = useMemo(() => (q.trim() ? { q: q.trim() } : {}), [q]);
  const { data, isLoading, isError, error } = useAdminUsers(filters);
  const users = data?.users ?? [];

  return (
    <div className="page admin-users-page">
      <header className="page-header">
        <div>
          <h1>User Accounts</h1>
          <p className="page-sub">Internal staff accounts, roles, and access.</p>
        </div>
        <Button variant="primary" onClick={() => setInviteOpen(true)}>
          Create user
        </Button>
      </header>

      <section className="filter-bar">
        <FormField label="Search" className="filter-search">
          <Input
            type="search"
            placeholder="Name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </FormField>
      </section>

      {isLoading && <div className="page-state">Loading users…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load users.'}
        </div>
      )}

      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Roles</Table.HeaderCell>
              <Table.HeaderCell>Person</Table.HeaderCell>
              <Table.HeaderCell>2FA</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Last login</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {users.length === 0 && (
              <Table.Empty colSpan={7}>No users match the current search.</Table.Empty>
            )}
            {users.map((u) => (
              <Table.Row key={u.id}>
                <Table.Cell tone="strong">
                  <Link to={`/admin/users/${u.id}`} className="table-link">{u.display_name}</Link>
                </Table.Cell>
                <Table.Cell>{u.email}{u.phone ? <div className="muted small">{formatPhoneDisplay(u.phone)}</div> : null}</Table.Cell>
                <Table.Cell tone="muted">{u.roles.length > 0 ? u.roles.join(', ') : '—'}</Table.Cell>
                <Table.Cell tone="muted">{u.person_name ?? '—'}</Table.Cell>
                <Table.Cell>{u.is_2fa_enabled ? <Badge tone="success">On</Badge> : '—'}</Table.Cell>
                <Table.Cell>
                  {u.is_active
                    ? <Badge tone="success">Active</Badge>
                    : <Badge tone="neutral">Inactive</Badge>}
                  {u.must_change_password && <Badge tone="info">Pwd reset</Badge>}
                </Table.Cell>
                <Table.Cell>{fmtDate(u.last_login_at)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {inviteOpen && <CreateUserModal onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

// =============================================================================
// Create modal — full account creation: email/name/phone, roles, a person link
// (existing or new), and credentials (email a temp password OR set one). When a
// temp password could not be emailed, the API returns it to relay out-of-band.
// =============================================================================

type PersonMode = 'none' | 'existing' | 'new';
type CredMode = 'email' | 'set';

function CreateUserModal({ onClose }: { onClose: () => void }): JSX.Element {
  const create = useCreateAdminUser();
  const { data: rolesData } = useRoles();
  const roles = rolesData?.roles ?? [];

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [roleIds, setRoleIds] = useState<number[]>([]);

  const [personMode, setPersonMode] = useState<PersonMode>('none');
  const [linkedPerson, setLinkedPerson] = useState<{ id: number; full_name: string } | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [credMode, setCredMode] = useState<CredMode>('email');
  const [password, setPassword] = useState('');
  const [forceChange, setForceChange] = useState(true);

  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const toggleRole = (id: number): void =>
    setRoleIds((cur) => (cur.includes(id) ? cur.filter((r) => r !== id) : [...cur, id]));

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const input: CreateAdminUserInput = {
      email: email.trim(),
      display_name: displayName.trim(),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(roleIds.length > 0 ? { role_ids: roleIds } : {}),
      ...(personMode === 'existing' && linkedPerson ? { person_id: linkedPerson.id } : {}),
      ...(personMode === 'new' && firstName.trim() && lastName.trim()
        ? { first_name: firstName.trim(), last_name: lastName.trim() }
        : {}),
      ...(credMode === 'set' ? { password } : {}),
      must_change_password: forceChange,
    };
    try {
      const res = await create.mutateAsync(input);
      if (res.temp_password) {
        setTempPassword(res.temp_password);
        toast.warn('User created. Email not sent — copy the temporary password.');
      } else {
        toast.success(res.emailed ? 'User created. A temporary password was emailed.' : 'User created.');
        onClose();
      }
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to create user.');
    }
  };

  const canSubmit =
    !!email.trim() &&
    !!displayName.trim() &&
    (personMode !== 'existing' || linkedPerson !== null) &&
    (credMode !== 'set' || password.length >= 12);

  return (
    <Dialog
      title="Create user"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        tempPassword ? (
          <Button variant="primary" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="create-user-form"
              variant="primary"
              disabled={create.isPending || !canSubmit}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </>
        )
      }
    >
      {tempPassword ? (
        <div className="form-grid">
          <p>The account was created but no email could be sent. Share this temporary password securely — the user must change it on first sign-in:</p>
          <code className="mono temp-password">{tempPassword}</code>
        </div>
      ) : (
        <form id="create-user-form" className="form-grid" onSubmit={onSubmit}>
          <FormField label="Email" required>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </FormField>
          <FormField label="Display name" required>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </FormField>
          <FormField label="Phone">
            <PhoneInput value={phone} onChange={setPhone} />
          </FormField>

          {/* Person link */}
          <fieldset className="form-field form-field-full">
            <span>Person</span>
            <div className="checkbox-list">
              <label className="checkbox-field">
                <input type="radio" name="person-mode" checked={personMode === 'none'} onChange={() => setPersonMode('none')} />
                <span>None</span>
              </label>
              <label className="checkbox-field">
                <input type="radio" name="person-mode" checked={personMode === 'existing'} onChange={() => setPersonMode('existing')} />
                <span>Link existing</span>
              </label>
              <label className="checkbox-field">
                <input type="radio" name="person-mode" checked={personMode === 'new'} onChange={() => setPersonMode('new')} />
                <span>Create new</span>
              </label>
            </div>
            {personMode === 'existing' && (
              linkedPerson ? (
                <p className="small">
                  Linked: <strong>{linkedPerson.full_name}</strong>{' '}
                  <Button variant="ghost" className="btn-sm" onClick={() => setLinkedPerson(null)}>change</Button>
                </p>
              ) : (
                <PersonPicker onSelect={(p) => setLinkedPerson(p)} />
              )
            )}
            {personMode === 'new' && (
              <div className="form-grid two-col">
                <Input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <Input placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            )}
          </fieldset>

          {/* Credentials */}
          <fieldset className="form-field form-field-full">
            <span>Credentials</span>
            <div className="checkbox-list">
              <label className="checkbox-field">
                <input type="radio" name="cred-mode" checked={credMode === 'email'} onChange={() => setCredMode('email')} />
                <span>Email a temporary password</span>
              </label>
              <label className="checkbox-field">
                <input type="radio" name="cred-mode" checked={credMode === 'set'} onChange={() => setCredMode('set')} />
                <span>Set a password</span>
              </label>
            </div>
            {credMode === 'set' && (
              <Input
                type="password"
                placeholder="At least 12 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={12}
                autoComplete="new-password"
              />
            )}
            <label className="checkbox-field">
              <input type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)} />
              <span>Require password change on next login</span>
            </label>
          </fieldset>

          {/* Roles */}
          <fieldset className="form-field form-field-full">
            <span>Roles</span>
            <div className="checkbox-list">
              {roles.map((r) => (
                <label key={r.id} className="checkbox-field">
                  <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
                  <span>{r.name}</span>
                </label>
              ))}
              {roles.length === 0 && <span className="muted small">No roles available.</span>}
            </div>
          </fieldset>
        </form>
      )}
    </Dialog>
  );
}
